import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the TableTransactionsByPlayerSecondLevelReports view.
 * @extends JetView
 */
export default class TableTransactionsByPlayerSecondLevelReports extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function

        /**
         * Formats a percentage value.
         * @param {number} value - The value to format.
         * @returns {string} The formatted percentage string.
         */
        function percentFormat(value) {
            return webix.i18n.numberFormat(value) + '%'; // Formatting value as percentage
        }

        return {
            rows: [
                {
                    cols: [
                        {
                            id: 'datatable', // ID for the datatable
                            view: 'datatable', // Defining a datatable view
                            scroll: 'xy', // Enabling both horizontal and vertical scrolling
                            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
                            columns: [
                                // Column definitions for the datatable
                                {
                                    id: 'Date',
                                    header: _('Fecha'), // Localized header
                                    width: 200,
                                    format: dateFormat, // Date formatting
                                },
                                {
                                    id: 'UniqueTransactionId',
                                    header: _('Identificador único de la transacción'), // Unique transaction ID header
                                    width: 300,
                                },
                                {
                                    id: 'PlayerId',
                                    header: _('Identificador único del jugador'), // Unique player ID header
                                    width: 250,
                                },
                                {
                                    id: 'PlayerName',
                                    header: _("Nombre del jugador"), // Player name header
                                    width: 350,
                                },
                                {
                                    id: 'DocumentType',
                                    header: _('Tipo de documento'), // Document type header
                                    width: 200,
                                },
                                {
                                    id: 'DocumentNumber',
                                    header: _('Numero de documento'), // Localized document number header
                                    width: 250,
                                },
                                {
                                    id: 'SportName',
                                    header: _('Nombre del deporte'), // Sport name header
                                    width: 250,
                                },
                                {
                                    id: 'CompetitionName',
                                    header: _('Nombre de la competencia'), // Competition name header
                                    width: 250,
                                },
                                {
                                    id: 'EventName',
                                    header: _('Nombre del evento'), // Event name header
                                    width: 250,
                                },
                                {
                                    id: 'MarketName',
                                    header: _('Nombre del mercado'), // Localized market name header
                                    width: 200,
                                },
                                {
                                    id: 'Selection',
                                    header: _('Selección'), // Localized selection header
                                    width: 200,
                                },
                                {
                                    id: 'Bet',
                                    header: _('Apuesta'), // Bet header
                                    width: 300,
                                },
                                {
                                    id: 'Award',
                                    header: _('Premio'), // Award header
                                    width: 300,
                                },
                                {
                                    id: 'Odds',
                                    header: _('Cuota'), // Odds header
                                    width: 150,
                                },
                                {
                                    id: 'TicketDate',
                                    header: _('Fecha de ticket'), // Localized ticket date header
                                    width: 250,
                                    format: webix.Date.dateToStr('%d/%m/%Y %H:%i:%s'), // Custom date format
                                },
                                {
                                    id: 'StartDate',
                                    header: _('Fecha de inicio'), // Localized start date header
                                    width: 250,
                                    format: webix.Date.dateToStr('%d/%m/%Y %H:%i:%s'), // Custom date format
                                },
                                {
                                    id: 'EndDate',
                                    header: _('Fecha de fin'), // Localized end date header
                                    width: 250,
                                    format: webix.Date.dateToStr('%d/%m/%Y %H:%i:%s'), // Custom date format
                                },
                                {
                                    id: 'TransactionLocation',
                                    header: _('Lugar de transacción'), // Localized transaction location header
                                    width: 250,
                                },
                            ],
                            on: {
                                // Event handlers for the datatable
                                onBeforeLoad: function () {
                                    this.showOverlay(
                                        // Show loading overlay before data load
                                        "<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                                    );
                                },
                                onAfterLoad: function () {
                                    this.hideOverlay(); // Hide loading overlay after data load
                                },
                            },
                            ready: function () {
                                if (!this.count()) {
                                    // Check if there are no data
                                    webix.extend(this, webix.OverlayBox); // Extend with overlay functionality
                                    this.showOverlay(
                                        // Show no data overlay
                                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                                            _('No hay datos') +
                                            '</div>',
                                    );
                                }
                            },
                            autowidth: false, // Disable automatic width adjustment
                            math: true, // Enable mathematical operations
                            datafetch: 10, // Number of records to fetch at once
                            pager: 'pagerA', // Pager ID for pagination
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The datatable instance.
     */
    init(grid) {
        grid = $$('datatable'); // Get the datatable instance
        let url = devConfig.geturlApi() + 'es/Financial/GetIncomeSecondLevel'; // API URL for data fetching

        // Event listener for filtering transactions
        this.on(this.app, 'transactionsByPlayerSecondLevel:filter', (filterData) => {
            let object = Object.entries(filterData); // Convert filter data to entries
            let string1 = ''; // Query string for API request

            for (let i = 0; i < object.length; i++) {
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    if (object[i][0] == 'Datepicker') {
                        // Handle date range filter
                        let date = object[i][1].split(' - ');
                        string1 = string1 + '&dateFrom=' + date[0];
                        string1 = string1 + '&dateTo=' + date[1];
                    } else {
                        if (object[i][0] != 'Sort' && object[i][0] != 'Dir' && object[i][0] != 'Date') {
                            string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Append other filters to query string
                        } else {
                            if (object[i][0] == 'Date') {
                                // Handle single date filter
                                let date = object[i][1].replace(' 00:00:00', '');
                                string1 = string1 + '&dateFrom=' + date;
                                string1 = string1 + '&dateTo=' + date;
                            }
                        }
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        // Handle object type filters
                        let object2 = Object.entries(object[i][1]);
                        for (let j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    // Handle start date
                                    let d = new Date(object2[j][1]);
                                    let dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Append start date to query string
                                } else {
                                    if (object2[j][0] === 'end') {
                                        // Handle end date
                                        let d = new Date(object2[j][1]);
                                        let dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Append end date to query string
                                    }
                                }
                            }
                        }
                    }
                }
            }
            grid.clearAll(); // Clear existing data in the grid
            $$('consultDesk').define('disabled', true); // Disable consult desk during loading
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + string1, // Load data from API
            )
                .then((response) => {
                    $$('consultDesk').define('disabled', false); // Re-enable consult desk after loading
                })
                .catch((e) => {
                    $$('consultDesk').define('disabled', false); // Re-enable consult desk on error
                });
            $$('filterPopup').hide(); // Hide filter popup
        });

        // Event listener for pagination changes
        $$('pagerA').attachEvent('onAfterPageChange', function (page) {
            webix.delay(
                function () {
                    if (page != '0') {
                        chargeTable(); // Load data for the new page
                    }
                },
                '',
                '',
                100,
            );
        });

        // Event listener for size changes
        this.on(this.app, 'changeSize', (filterData) => {
            chargeTable(); // Reload table data on size change
        });

        /**
         * Loads data into the grid based on pagination.
         */
        function chargeTable() {
            let size = grid.getPager().config.size; // Get the size of the pager
            let page = grid.getPage(); // Get the current page
            let first = size * page; // Calculate the starting index
            let last = first + size - 1; // Calculate the ending index
            grid.load(
                url + '?start=' + first + '&count=' + (last + 1) + filter, // Load data from API with pagination
            );
        }
    }
}

// FILE DOCUMENTED
